/**************************************************************
 * main.js — CLEAN / ORGANIZED (same behavior, fewer bugs)
 * Goals:
 * - ONE pricing system (no applyMemberPrices)
 * - Clear sections + guardrails (no double-binding)
 * - Login/Logout always refreshes pricing correctly
 **************************************************************/

console.log("main.js loaded ✅");
console.log("CC main loaded", Date.now());

/* ============================================================
   0) CONSTANTS
============================================================ */
const API_BASE = "https://v1.ecolizo.com";
const CC_STORAGE_KEY = "cc_session";
const CC_ADDRESS_KEY = "cc_address";
const CC_THANKYOU_PAGE_HANDLE_DEFAULT = "confirmation-d-achat";
const CC_BENEFITS_KEY = "cc_benefits_cache";

// IMPORTANT: never discount this product handle
const CC_ADVANTAGES_HANDLE = "abonnement-avantages";

/* ============================================================
   1) SMALL GENERIC HELPERS
============================================================ */
const $ = (sel, root = document) => root.querySelector(sel);
const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));

/**
 * Decode HTML entities (&#39; &amp; &lt; etc.) to their actual characters.
 * Prevents double-encoding when data passes through APIs or Shopify attributes.
 */
function decodeEntities(str) {
  if (!str || typeof str !== 'string') return str || '';
  if (!/&\w+;|&#\d+;|&#x[\da-f]+;/i.test(str)) return str; // fast path: no entities
  var el = document.createElement('textarea');
  el.innerHTML = str;
  return el.value;
}

/**
 * Clean raw API message text: unescape backslash sequences (e.g. \' → ') and decode HTML entities.
 */
function cleanApiMsg(s) {
  return decodeEntities(String(s || '').replace(/\\'/g, "'").replace(/\\"/g, '"').replace(/\\\\/g, '\\'));
}

function jsonToBase64Utf8(obj) {
  const json = JSON.stringify(obj || {});
  return btoa(unescape(encodeURIComponent(json))); // UTF-8 safe base64
}

function getSubmitter(e) {
  return e.submitter || document.activeElement;
}

function getShopifyLocale() {
  const lang = (document.documentElement.getAttribute("lang") || "").trim();
  if (!lang) return "fr";
  return lang.split("-")[0].toLowerCase();
}

/* ============================================================
   2) BENEFITS CACHE
============================================================ */
function getBenefitsCache() {
  try {
    return JSON.parse(localStorage.getItem(CC_BENEFITS_KEY) || "null");
  } catch {
    return null;
  }
}
function setBenefitsCache(obj) {
  localStorage.setItem(CC_BENEFITS_KEY, JSON.stringify(obj));
}
function clearBenefitsCache() {
  localStorage.removeItem(CC_BENEFITS_KEY);
  // Also clear in-memory state
  __ccCurrentHasBenefits = false;
  __ccBenefitsPromise = null;
  __ccBenefitsTs = 0;
  __ccLastWrittenHasSub = null;
}

/* ============================================================
   3) API HELPERS
============================================================ */
async function postJSON(pathOrUrl, payload) {
  const shopify_locale = getShopifyLocale();
  const body = { ...(payload || {}), shopify_locale };

  const url = /^https?:\/\//i.test(String(pathOrUrl || ""))
    ? String(pathOrUrl)
    : `${API_BASE}${String(pathOrUrl || "")}`;

  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "same-origin",
    body: JSON.stringify(body),
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(decodeEntities(data.message || data.error) || (window.SHOP_TRANSLATIONS?.errors?.server || "Erreur serveur"));
  return data;
}

async function hasActiveSubscription(email) {
  const cleanEmail = String(email || "").trim();
  if (!cleanEmail) return false;

  const resp = await postJSON("/verifier_abo_statut.php", { email: cleanEmail });

  // ✅ UPDATED: Check for the three subscription statuses (TRIAL, ACTIVE, RECYCLE_BILLING)
  if (resp?.status) {
    const status = String(resp.status || "").toUpperCase();
    return status === "TRIAL" || status === "ACTIVE" || status === "RECYCLE_BILLING";
  }

  // Fallback to boolean check
  const v = resp?.hasActiveSubscription ?? resp?.success;
  return v === 1 || v === "1" || v === true || v === "true";
}

/* ============================================================
   4) LOCAL STORAGE (SESSION + ADDRESS)
============================================================ */
const CC_SESSION_TTL = 24 * 60 * 60 * 1000; // 24 hours

function saveSession(email, orderId, language = "fr") {
  localStorage.setItem(
    CC_STORAGE_KEY,
    JSON.stringify({ email, orderId, language, createdAt: Date.now() })
  );
}

function getSession() {
  try {
    const session = JSON.parse(localStorage.getItem(CC_STORAGE_KEY) || "null");
    if (!session) return null;

    // Expire after 24 hours
    if (session.createdAt && (Date.now() - session.createdAt) > CC_SESSION_TTL) {
      clearSession();
      clearBenefitsCache();
      return null;
    }

    return session;
  } catch {
    return null;
  }
}

function patchSession(patch) {
  const cur = getSession() || {};
  const next = { ...cur, ...patch };
  localStorage.setItem(CC_STORAGE_KEY, JSON.stringify(next));
  return next;
}

function clearSession() {
  localStorage.removeItem(CC_STORAGE_KEY);
}

/* ============================================================
   4.5) CLEAN CART ATTRIBUTES IF USER NOT LOGGED IN
============================================================ */
async function cleanCartAttributesIfNotLoggedIn() {
  // Check if user is really logged in (has session with email AND orderId)
  const session = getSession();
  const isReallyLoggedIn = !!(session?.email && session?.orderId);
  
  if (!isReallyLoggedIn) {
    // User is not logged in, clean cart attributes and cache
    try {
      const cart = await shopifyGetCart();
      const needsCleanup = 
        cart?.attributes?.cc_has_subscription === "1" ||
        cart?.attributes?.cc_has_subscription_choice === "1" ||
        cart?.attributes?.cc_subscription_choice === "1";
      
      if (needsCleanup) {
        // Clear cart attributes
        await shopifyCartUpdateAttributes({
          cc_has_subscription: "0",
          cc_has_subscription_choice: "0",
          cc_subscription_choice: "0"
        });
        
        // Clear benefits cache
        clearBenefitsCache();
        
        console.log('[CC] Cleaned cart attributes and cache - user not logged in');
        
        // Refresh cart display if on cart page or drawer is open
        if (document.body.classList.contains('template-cart') || document.getElementById('CartDrawer')) {
          if (typeof theme?.cart?.getCartProductMarkup === 'function') {
            try {
              const cartMarkup = await theme.cart.getCartProductMarkup();
              const cartContainer = document.querySelector('[data-products]');
              if (cartContainer && cartMarkup) {
                cartContainer.innerHTML = cartMarkup;
              }
            } catch (e) {
              console.warn('[CC] Cart refresh failed after cleanup:', e);
            }
          }
        }
        
        // Refresh pricing
        if (window.CC_REFRESH_PRICING) {
          await window.CC_REFRESH_PRICING();
        }
      }
    } catch (e) {
      console.warn('[CC] Failed to clean cart attributes:', e);
    }
  }
}

function saveAddress(address) {
  localStorage.setItem(CC_ADDRESS_KEY, JSON.stringify(address));
}

function getAddress() {
  try {
    return JSON.parse(localStorage.getItem(CC_ADDRESS_KEY) || "null");
  } catch {
    return null;
  }
}

/* ============================================================
   5) SHOPIFY CART API HELPERS
============================================================ */

async function shopifyCartUpdateAttributes(attrs) {
  const res = await fetch("/cart/update.js", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "same-origin",
    body: JSON.stringify({ attributes: attrs }),
  });

  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json?.description || (window.SHOP_TRANSLATIONS?.errors?.shopify_cart_update || "Erreur Shopify: cart/update.js"));
  return json;
}

async function shopifyGetCart() {
  const res = await fetch("/cart.js", { credentials: "same-origin" });
  if (!res.ok) throw new Error(window.SHOP_TRANSLATIONS?.errors?.shopify_cart || "Erreur Shopify: /cart.js");
  return res.json();
}

async function shopifyRemoveLineItemByKey(lineKey) {
  const res = await fetch("/cart/change.js", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "same-origin",
    body: JSON.stringify({ id: lineKey, quantity: 0 }),
  });

  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json?.description || (window.SHOP_TRANSLATIONS?.errors?.shopify_cart_change || "Erreur Shopify: cart/change.js"));
  return json;
}

async function shopifyAddVariantToCart(variantId, quantity = 1) {
  const res = await fetch("/cart/add.js", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "same-origin",
    body: JSON.stringify({
      items: [{ id: Number(variantId), quantity: Number(quantity) }],
    }),
  });

  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json?.description || (window.SHOP_TRANSLATIONS?.errors?.shopify_cart_add || "Erreur Shopify: /cart/add.js"));
  return json;
}

/* ============================================================
   6) GLOBAL PRICING (single source of truth)
   - Uses [data-cc-price-cents] from Liquid
   - Excludes subscription variant + advantages handle
============================================================ */

/**
 * Unified money formatter.
 * Uses theme.Currency.formatMoney (same as theme.js) when available,
 * falls back to a simple EUR formatter for safety.
 */
function ccFormatMoney(cents) {
  try {
    if (window.theme?.Currency?.formatMoney && window.theme?.settings?.moneyFormat) {
      return theme.Currency.formatMoney(cents, theme.settings.moneyFormat);
    }
  } catch (e) { /* fall through */ }

  // Fallback: basic EUR formatting  (FR locale: "17,50 €")
  const n = Number(cents);
  if (!Number.isFinite(n)) return "0,00 €";
  const euros = (n / 100).toFixed(2).replace(".", ",");
  return euros + " €";
}

function getBenefitsEmailCandidate() {
  const sessionEmail = String((getSession() || {}).email || "").trim();
  if (sessionEmail) return sessionEmail;

  // ✅ UPDATED: Only use cart email if user is logged in (has session)
  // This prevents API calls for non-logged-in users who just entered an email in the cart
  // The 37.50€ discount should only apply to logged-in subscribers anyway
  // Removed fallback to cart drawer email input to avoid unnecessary API calls

  return "";
}

// Store current benefits state (only recalculate when cart actually changes)
let __ccCurrentHasBenefits = false;

// In-memory promise cache — deduplicates concurrent/rapid calls (5s window)
let __ccBenefitsPromise = null;
let __ccBenefitsTs = 0;
const __ccBenefitsTTL = 5000; // ms

// Tracks the last value written to cart attr cc_has_subscription — avoids redundant POSTs
let __ccLastWrittenHasSub = null;

async function computeHasBenefits() {
  // Return cached promise if still in-flight or resolved within TTL
  if (__ccBenefitsPromise && (Date.now() - __ccBenefitsTs) < __ccBenefitsTTL) {
    return __ccBenefitsPromise;
  }

  __ccBenefitsTs = Date.now();
  __ccBenefitsPromise = _computeHasBenefitsImpl();

  // On rejection, clear cache so next call retries
  __ccBenefitsPromise.catch(() => {
    __ccBenefitsPromise = null;
    __ccBenefitsTs = 0;
  });

  return __ccBenefitsPromise;
}

async function _computeHasBenefitsImpl() {
  // ✅ UPDATED: Only check benefits if user is really logged in (has session with email AND orderId)
  // This prevents prices from staying reduced after checkout when user goes back
  const session = getSession();
  const isReallyLoggedIn = !!(session?.email && session?.orderId);
  
  if (!isReallyLoggedIn) {
    // User is not really logged in, don't apply benefits even if cart attributes say so
    __ccCurrentHasBenefits = false;
    return false;
  }

  // 1) cart attributes can force benefits (set by routing) - but only if user is logged in
  try {
    const cart = await shopifyGetCart();
    const v =
      cart?.attributes?.cc_has_subscription === "1" ||
      cart?.attributes?.cc_has_subscription_choice === "1" ||
      cart?.attributes?.cc_subscription_choice === "1";
    if (v) {
      __ccCurrentHasBenefits = true;
      return true;
    }
  } catch {}

  // 2) else API check if we have email (from session)
  const email = getBenefitsEmailCandidate();
  if (!email) {
    __ccCurrentHasBenefits = false;
    return false;
  }

  try {
    const result = await hasActiveSubscription(email);
    __ccCurrentHasBenefits = result;
    return result;
  } catch {
    __ccCurrentHasBenefits = false;
    return false;
  }
}

async function applyDiscountToDomPrices({ hasBenefits }) {
  // ✅ SIMPLIFIED: Liquid templates now handle price display
  // We only need to:
  // 1. Set cart attributes so Liquid can read them
  // 2. Update data-cc-final-cents for subtotal calculation (if needed)
  
  const nodes = $$("[data-cc-price-cents]");
  if (!nodes.length) {
    // Still set cart attributes even if no price elements found (skip if unchanged)
    const newVal = hasBenefits ? "1" : "0";
    if (__ccLastWrittenHasSub !== newVal) {
      try {
        await shopifyCartUpdateAttributes({ cc_has_subscription: newVal });
        __ccLastWrittenHasSub = newVal;
      } catch (e) {
        console.warn('[CC] Failed to update cart attributes:', e);
      }
    }
    return;
  }

  const subVariantId = String(window.CC_SUB_VARIANT_ID || "").trim();

  nodes.forEach((el) => {
    const baseCents = Number(el.getAttribute("data-cc-price-cents"));
    if (!Number.isFinite(baseCents) || baseCents <= 0) return;

    const variantId = String(el.getAttribute("data-cc-variant-id") || "").trim();
    const handle = String(el.getAttribute("data-cc-handle") || "").trim();

    const isSubscriptionProduct =
      subVariantId && variantId && variantId === subVariantId;

    const isAdvantagesProduct = handle === CC_ADVANTAGES_HANDLE;

    // Skip ALL cart items — the Liquid template already renders the correct
    // (member-discounted) prices in data-cc-final-cents for every cart item.
    // Applying JS 50% on top of already-discounted prices causes a brief
    // half-price flicker in the subtotal before the DOM rebuilds.
    const isInCart = el.closest('.cart__item') !== null;
    if (isInCart) return;

    // ✅ UPDATED: Subscription product (abonnement-avantages) should always show real price on product page.
    // The 0€ price is only applied in checkout (handled in page.checkout.liquid).
    // If user has benefits, they get 50% off other products, but subscription always shows full price here.
    let finalCents = baseCents;
    if (hasBenefits && !isSubscriptionProduct && !isAdvantagesProduct) {
      // Apply 50% discount to non-subscription products when user has benefits
      finalCents = Math.round(baseCents / 2);
    }
    // Note: isAdvantagesProduct keeps original price (baseCents) - not set to 0

    // ✅ CRITICAL: Prevent loops (observer) - check if already processed with same values
    const prevFinalCents = Number(el.dataset.ccFinalCents || "");
    const prevApplied = el.dataset.ccPriceApplied || "";
    const currentApplied = hasBenefits ? "benefits" : "base";
    
    // Skip if price and status haven't changed
    if (Number.isFinite(prevFinalCents) && prevFinalCents === finalCents && prevApplied === currentApplied) {
      return;
    }

    // ✅ SIMPLIFIED: Only update data-cc-final-cents for subtotal calculation
    // DON'T modify textContent - Liquid templates handle display!
    el.setAttribute('data-cc-final-cents', String(finalCents));
    el.setAttribute('data-cc-price-applied', hasBenefits ? "benefits" : "base");
  });
  
  // Set cart attribute so Liquid templates can read it (skip if unchanged)
  const newVal = hasBenefits ? "1" : "0";
  if (__ccLastWrittenHasSub !== newVal) {
    try {
      await shopifyCartUpdateAttributes({ cc_has_subscription: newVal });
      __ccLastWrittenHasSub = newVal;
    } catch (e) {
      console.warn('[CC] Failed to update cart attributes:', e);
    }
  }
  
}

async function initGlobalPricing() {
  // ── Resolve benefits with TTL cache (single pass on load) ──
  const session = getSession();
  const email = String(session?.email || "").trim();
  const TTL = 10 * 60 * 1000;
  const now = Date.now();
  const cache = getBenefitsCache();

  let hasBenefits = false;

  if (email && cache && cache.email === email && now - Number(cache.checkedAt || 0) < TTL) {
    // Cache hit — skip API call
    hasBenefits = !!cache.active;
  } else if (email) {
    // Cache miss or stale — check API, then cache result
    try {
      const active = await hasActiveSubscription(email);
      setBenefitsCache({ email, active: !!active, checkedAt: Date.now() });
      hasBenefits = !!active;
    } catch (e) {
      console.warn("[CC] initGlobalPricing benefits check failed:", e);
    }
  }

  // Cart attributes may override (set by upsell routing: cc_subscription_choice="1")
  // Check directly instead of calling computeHasBenefits() which would re-call the API
  if (!hasBenefits) {
    try {
      const cart = await shopifyGetCart();
      hasBenefits =
        cart?.attributes?.cc_has_subscription === "1" ||
        cart?.attributes?.cc_has_subscription_choice === "1" ||
        cart?.attributes?.cc_subscription_choice === "1";
    } catch {}
  }

  __ccCurrentHasBenefits = hasBenefits;
  setBenefitsState(hasBenefits);
  await applyDiscountToDomPrices({ hasBenefits });

  // ── CC_REFRESH_PRICING: re-usable for login, logout, cart changes ──
  window.CC_REFRESH_PRICING = async () => {
    hasBenefits = await computeHasBenefits();
    setBenefitsState(hasBenefits);
    await applyDiscountToDomPrices({ hasBenefits });
    
    // If on cart page or drawer is present, reload cart HTML to show updated Liquid prices
    if (document.body.classList.contains('template-cart') || document.getElementById('CartDrawer')) {
      if (typeof theme?.cart?.getCartProductMarkup === 'function') {
        try {
          const cartMarkup = await theme.cart.getCartProductMarkup();
          const cartContainer = document.querySelector('[data-products]');
          if (cartContainer && cartMarkup) {
            cartContainer.innerHTML = cartMarkup;
          }
        } catch (e) {
          console.warn('[CC] Cart refresh failed, prices will update on next page load:', e);
        }
      }
    }
  };
}

/* ============================================================
   7) BENEFITS STATE (HTML class + cache)
   - Used by UI + fallback only
============================================================ */
function setBenefitsState(active) {
  window.CC_HAS_BENEFITS = !!active;
  document.documentElement.classList.toggle("cc-has-benefits", !!active);
}

async function resolveBenefitsAndApply() {
  setBenefitsState(false);

  const session = getSession();
  const email = String(session?.email || "").trim();
  if (!email) return false;

  const TTL = 10 * 60 * 1000;
  const now = Date.now();
  const cache = getBenefitsCache();

  if (cache && cache.email === email && now - Number(cache.checkedAt || 0) < TTL) {
    const active = !!cache.active;
    setBenefitsState(active);
    await window.CC_REFRESH_PRICING?.();
    return active;
  }

  try {
    const active = await hasActiveSubscription(email);
    setBenefitsCache({ email, active: !!active, checkedAt: now });
    setBenefitsState(!!active);
    await window.CC_REFRESH_PRICING?.();
    return !!active;
  } catch (e) {
    console.warn("[CC] resolveBenefits failed:", e);
    return false;
  }
}

/* ============================================================
   8) CART DRAWER — UI HELPERS
============================================================ */
function initCartDrawerAddressAccordion() {
  const btn = $("#CartDrawer .cc-addr-toggle");
  const body = $("#CartDrawer #cc-addr-body");
  if (!btn || !body) return;

  if (btn.dataset.bound === "1") return;
  btn.dataset.bound = "1";

  btn.addEventListener("click", () => {
    const open = btn.getAttribute("aria-expanded") === "true";
    btn.setAttribute("aria-expanded", open ? "false" : "true");
    body.hidden = open;
  });
}

function scrollCartDrawerToMsg() {
  const drawer = $("#CartDrawer");
  if (!drawer) return;

  const msg = drawer.querySelector(".cc-cart-address-msg");
  if (!msg) return;

  if (!msg.textContent.trim()) return;

  // Try to find the scrollable container (cart-drawer__row or drawer__scrollable)
  const rowContainer = drawer.querySelector(".cart-drawer__row");
  const scroller = rowContainer || drawer.querySelector(".drawer__scrollable");
  
  if (!scroller) return;

  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      // Get the position relative to the scrollable container
      const msgRect = msg.getBoundingClientRect();
      const scrollerRect = scroller.getBoundingClientRect();
      const scrollTop = scroller.scrollTop;
      const relativeTop = msgRect.top - scrollerRect.top + scrollTop;
      
      scroller.scrollTo({ 
        top: Math.max(0, relativeTop - 20), 
        behavior: "smooth" 
      });
    });
  });
}

function setCartDrawerMsg(text) {
  const el = $("#CartDrawer .cc-cart-address-msg");
  if (el) el.textContent = decodeEntities(text) || "";
  if (text) scrollCartDrawerToMsg();
}

function setCartCheckoutLoading(isLoading) {
  const form = $("#CartDrawerForm");
  const btn = $('#CartDrawer [type="submit"][name="checkout"]');
  if (!btn) return;

  const footer = btn.closest(".cart__checkout-wrapper") || btn.parentElement;
  const addressBlock = form?.querySelector(".cc-cart-address");

  // Helper: strip loading classes from the address block once
  const stripAddressLoading = () => {
    if (addressBlock) {
      addressBlock.classList.remove("is-loading", "loading", "is-submitting");
    }
  };

  if (isLoading) {
    btn.dataset.ccPrevDisabled = btn.disabled ? "1" : "0";
    btn.disabled = true;
    btn.setAttribute("aria-busy", "true");
    btn.classList.add("btn--loading");
    footer?.classList.add("is-loading", "loading");
    form?.classList.add("is-loading", "loading");

    stripAddressLoading();

    // Watch for anything re-adding loading classes to the address block
    if (addressBlock && !btn._ccAddrObserver) {
      const mo = new MutationObserver(() => stripAddressLoading());
      mo.observe(addressBlock, { attributes: true, attributeFilter: ["class"] });
      btn._ccAddrObserver = mo;
    }
  } else {
    const wasDisabled = btn.dataset.ccPrevDisabled === "1";
    btn.disabled = wasDisabled;
    btn.removeAttribute("aria-busy");
    delete btn.dataset.ccPrevDisabled;
    btn.classList.remove("btn--loading");
    footer?.classList.remove("is-loading", "loading");
    form?.classList.remove("is-loading", "loading");

    // Disconnect observer
    if (btn._ccAddrObserver) {
      btn._ccAddrObserver.disconnect();
      delete btn._ccAddrObserver;
    }

    stripAddressLoading();
  }
}

function readCartDrawerAddress() {
  const root = $("#CartDrawer .cc-cart-address");
  if (!root) return { ok: false, message: window.SHOP_TRANSLATIONS?.popup?.message_address_block_not_found || "Bloc adresse introuvable dans le panier." };

  const get = (name) => decodeEntities(root.querySelector(`[name="${name}"]`)?.value?.trim() || "");

  const data = {
    emailAddress: get("cc_email"),
    firstName: get("cc_firstName"),
    lastName: get("cc_lastName"),
    address1: get("cc_address1"),
    postalCode: get("cc_postalCode"),
    city: get("cc_city"),
    countryCode: get("cc_country"),
    phone: get("cc_phone"),
  };

  const T = window.SHOP_TRANSLATIONS?.popup || {};
  const missing = [];
  if (!data.firstName) missing.push(T.address_field_first_name || "Prénom");
  if (!data.lastName) missing.push(T.address_field_last_name || "Nom");
  if (!data.emailAddress) missing.push(T.address_field_email || "Email");
  if (!data.address1) missing.push(T.address_field_address || "Adresse");
  if (!data.postalCode) missing.push(T.address_field_postal_code || "Code postal");
  if (!data.city) missing.push(T.address_field_city || "Ville");
  if (!data.countryCode) missing.push(T.address_field_country || "Pays");

  if (missing.length) {
    const tpl = T.address_incomplete || "Veuillez compléter l'adresse de livraison : {{ fields }}.";
    return { ok: false, message: tpl.replace(/\{\{\s*fields\s*\}\}/, missing.join(", ")) };
  }

  data.phone = String(data.phone || "").trim();
  return { ok: true, data };
}

/* ============================================================
   9) SUBSCRIPTION / ROUTING HELPERS
============================================================ */
function getCheckoutResetAttrs() {
  const campaignId =
    window.CC_DEFAULT_CAMPAIGN_ID != null ? String(window.CC_DEFAULT_CAMPAIGN_ID) : "";
  const shipProfileId =
    window.CC_DEFAULT_SHIP_PROFILE_ID != null ? String(window.CC_DEFAULT_SHIP_PROFILE_ID) : "";

  return {
    cc_has_subscription: "0",
    cc_forceMerchantId: "",
    cc_pixel: "",
    cc_originalCampaignId: campaignId || "",
    cc_shipProfileId: shipProfileId || "",
    cc_subscription_choice: "",
    cc_has_subscription_choice: "0",
  };
}

function cartHasSubscriptionProduct(cart, subVariantId) {
  const v = String(subVariantId || "").trim();
  if (!v) return false;
  return !!cart?.items?.some((it) => String(it.variant_id) === v);
}

// ✅ NEW: Remove advantages product from cart if user has benefits
async function removeAdvantagesProductFromCartIfBenefits(hasBenefits = null) {
  // If hasBenefits is not provided, compute it (but prefer cart attributes to avoid API calls)
  if (hasBenefits === null) {
    hasBenefits = await computeHasBenefits();
  }
  if (!hasBenefits) return;

  try {
    const cart = await shopifyGetCart();
    const advantagesItem = cart.items?.find((item) => {
      const handle = String(
        item.product_handle || 
        item.handle || 
        item.product?.handle || 
        ""
      ).trim();
      return handle === CC_ADVANTAGES_HANDLE;
    });

    if (advantagesItem?.key) {
      await shopifyRemoveLineItemByKey(advantagesItem.key);
      console.log("[CC] Produit avantages supprimé du panier (utilisateur a les avantages)");
    }
  } catch (e) {
    console.warn("[CC] Échec de la suppression du produit avantages:", e);
  }
}

async function routeFromCartDrawerCheckout() {
  const localePrefix = typeof getLocalePrefix === 'function' ? getLocalePrefix() : (window.LOCALE_PREFIX || '');
  const checkoutUrl = window.CC_CHECKOUT_URL || localePrefix + "/pages/checkout";
  const upsellUrl = window.CC_UPSELL_URL || localePrefix + "/pages/offre-avantages";

  const subVariantId = String(window.CC_SUB_VARIANT_ID || "").trim();
  if (!subVariantId) throw new Error(window.SHOP_TRANSLATIONS?.errors?.missing_checkout_variant_id || "checkout_variant_id manquant dans les settings.");

  const cart = await shopifyGetCart();

  // ✅ Check if user was really logged in (has session with email AND orderId)
  const session = getSession();
  const wasReallyLoggedIn = !!(session?.email && session?.orderId);

  // ✅ Store flag to clean up after checkout if user was not really logged in
  if (!wasReallyLoggedIn) {
    sessionStorage.setItem("cc_cleanup_after_checkout", "1");
  }

  const email =
    String((session || {}).email || "").trim() ||
    String(cart?.attributes?.cc_email || "").trim();

  if (!email) throw new Error(window.SHOP_TRANSLATIONS?.errors?.email_not_found || "Email introuvable pour vérifier l'abonnement.");

  const active = await hasActiveSubscription(email);

  const subLine = cart.items?.find((it) => String(it.variant_id) === subVariantId);
  const subInCart = !!subLine;
  
  // ✅ CHECK IF USER ALREADY DECIDED (skip = "0", accept = "1")
  const userAlreadyDecided = cart?.attributes?.cc_subscription_choice === "0" || 
                             cart?.attributes?.cc_subscription_choice === "1";

  await shopifyCartUpdateAttributes({
    cc_has_subscription: active ? "1" : "0",
    cc_has_subscription_choice: subInCart ? "1" : "0",
  });

  if (active) {
    if (subInCart) await shopifyRemoveLineItemByKey(subLine.key);
    
    // ✅ NEW: Remove advantages product if user has benefits (pass active to avoid extra API call)
    await removeAdvantagesProductFromCartIfBenefits(active);
    
    window.location.href = checkoutUrl;
    return;
  }

  // ✅ FIXED: Only show upsell if user HASN'T made a choice yet
  if (subInCart || userAlreadyDecided) {
    window.location.href = checkoutUrl;
  } else {
    window.location.href = upsellUrl;
  }
}

/* ============================================================
   10) CART DRAWER — CHECKOUT SUBMIT BINDING
============================================================ */
function initCartDrawerCheckout() {
  if (document.documentElement.dataset.ccCartBound === "1") return;
  document.documentElement.dataset.ccCartBound = "1";

  document.addEventListener(
    "submit",
    async (e) => {
      const form = e.target;
      if (!form) return;

      const isDrawerForm = form.id === "CartDrawerForm";
      const isPageForm   = form.id === "CartPageForm";
      if (!isDrawerForm && !isPageForm) return;

      // Only intercept the checkout button, not other form submits (e.g. cart update)
      const submitter = getSubmitter(e);
      const isCheckout =
        (submitter && submitter.getAttribute("name") === "checkout") ||
        (isPageForm && !!form.querySelector('[name="checkout"]'));

      if (!isCheckout) return;

      e.preventDefault();
      e.stopPropagation();

      // ── CART PAGE FORM ──────────────────────────────────────────────────────
      // No address form on the cart page — just handle terms then route.
      if (isPageForm) {
        const localePrefix = typeof getLocalePrefix === "function" ? getLocalePrefix() : (window.LOCALE_PREFIX || "");
        const checkoutUrl  = window.CC_CHECKOUT_URL || localePrefix + "/pages/checkout";

        // Terms checkbox (cart page uses id="CartTerms")
        if (submitter && submitter.getAttribute("data-terms-required") === "true") {
          const terms = document.getElementById("CartTerms");
          if (terms && !terms.checked) {
            alert(window.SHOP_TRANSLATIONS?.popup?.message_accept_terms || window.theme?.strings?.cartTermsConfirmation || "Veuillez accepter les conditions.");
            submitter.classList.remove("btn--loading");
            return;
          }
        }

        try {
          await routeFromCartDrawerCheckout();
        } catch (err) {
          console.error(err);
          // No session / email available — go straight to the custom checkout
          // which will collect any missing information.
          window.location.href = checkoutUrl;
        }
        return;
      }

      // ── CART DRAWER FORM ────────────────────────────────────────────────────
      // Honeypot
      const hp = form.querySelector('input[name="company"]');
      if (hp && String(hp.value || "").trim() !== "") {
        setCartDrawerMsg(window.SHOP_TRANSLATIONS?.popup?.message_error_occurred || "Une erreur est survenue. Veuillez réessayer.");
        return;
      }

      setCartCheckoutLoading(true);

      try {
        const termsRequired = submitter && submitter.getAttribute("data-terms-required") === "true";
        if (termsRequired) {
          const terms = $("#CartTermsDrawer");
          if (terms && !terms.checked) {
            setCartDrawerMsg(window.SHOP_TRANSLATIONS?.popup?.message_accept_terms || "Veuillez accepter les conditions.");
            return;
          }
        }

        setCartDrawerMsg("");

        const addr = readCartDrawerAddress();
        if (!addr.ok) {
          setCartDrawerMsg(addr.message || window.SHOP_TRANSLATIONS?.popup?.message_fill_delivery_info || "Veuillez remplir vos informations de livraison.");
          return;
        }

        // Validation serveur : email (NeverBounce) + code postal
        if (window.CC_VERIFY_ENDPOINT) {
          const verifyPayload = {
            emailAddress:       addr.data.emailAddress,
            shippingFirstName:  addr.data.firstName,
            shippingLastName:   addr.data.lastName,
            shippingAddress1:   addr.data.address1,
            shippingAddress2:   "",
            shippingPostalCode: addr.data.postalCode,
            shippingCity:       addr.data.city,
            shippingCountry:    addr.data.countryCode,
            sessionId:          getCartIdFromCookie() || ""
          };
          let verifyResult;
          try {
            const vResp = await fetch(window.CC_VERIFY_ENDPOINT, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              credentials: "omit",
              body: JSON.stringify(verifyPayload)
            });
            verifyResult = vResp.ok ? await vResp.json() : null;
          } catch (_e) {
            verifyResult = null;
          }
          const emailErr  = verifyResult?.errors?.emailAddress;
          const postalErr = verifyResult?.errors?.shippingPostalCode;
          if (emailErr || postalErr) {
            setCartDrawerMsg(emailErr || window.SHOP_TRANSLATIONS?.popup?.error_postal_invalid || postalErr);
            return;
          }
        }

        const phone = String(addr?.data?.phone || "").trim();

        // Single batched call: reset context + save address
        await shopifyCartUpdateAttributes({
          ...getCheckoutResetAttrs(),
          cc_email: addr.data.emailAddress,
          cc_firstName: addr.data.firstName,
          cc_lastName: addr.data.lastName,
          cc_address1: addr.data.address1,
          cc_postalCode: addr.data.postalCode,
          cc_city: addr.data.city,
          cc_countryCode: addr.data.countryCode,
          cc_phone: phone,
        });

        setCartDrawerMsg("");
        await routeFromCartDrawerCheckout();
      } catch (err) {
        console.error(err);
        setCartDrawerMsg(err?.message || window.SHOP_TRANSLATIONS?.popup?.message_checkout_error || "Erreur pendant le checkout.");
      } finally {
        setCartCheckoutLoading(false);
      }
    },
    true
  );
}

/* ============================================================
   11) UPSELL PAGE
============================================================ */
function setUpsellMsg(text) {
  const el = $("#cc-upsell-msg");
  if (el) el.textContent = decodeEntities(text) || "";
}

function setBtnLoading(btn, isLoading) {
  if (!btn) return;
  if (isLoading) {
    btn.dataset.ccPrevDisabled = btn.disabled ? "1" : "0";
    btn.disabled = true;
    btn.setAttribute("aria-busy", "true");
  } else {
    const wasDisabled = btn.dataset.ccPrevDisabled === "1";
    btn.disabled = wasDisabled;
    btn.removeAttribute("aria-busy");
    delete btn.dataset.ccPrevDisabled;
  }
}

function initUpsellPage() {
  const addBtn = $("#cc-upsell-add-sub");
  const skipBtn = $("#cc-upsell-skip");
  if (!addBtn && !skipBtn) return;

  const localePrefix = typeof getLocalePrefix === 'function' ? getLocalePrefix() : (window.LOCALE_PREFIX || '');
  const checkoutUrl = window.CC_CHECKOUT_URL || localePrefix + "/pages/checkout";

  const resolveVariantId = () => {
    const raw = addBtn?.dataset?.variantId || window.CC_SUB_VARIANT_ID;
    const id = Number(raw);
    return Number.isFinite(id) && id > 0 ? id : null;
  };

  async function removeSubscriptionFromCartIfPresent() {
    const variantId = resolveVariantId();
    if (!variantId) return;

    const cart = await shopifyGetCart();
    const subLine = cart.items?.find((it) => String(it.variant_id) === String(variantId));
    if (subLine?.key) await shopifyRemoveLineItemByKey(subLine.key);
  }

  if (addBtn && addBtn.dataset.bound !== "1") {
    addBtn.dataset.bound = "1";
    addBtn.addEventListener("click", async () => {
      setUpsellMsg("");
      setBtnLoading(addBtn, true);

      try {
        const variantId = resolveVariantId();
        if (!variantId) throw new Error(window.SHOP_TRANSLATIONS?.errors?.subscription_variant_not_found || "Variant ID abonnement introuvable (settings.checkout_variant_id).");

        const cart = await shopifyGetCart();
        const already = cart.items?.some((it) => String(it.variant_id) === String(variantId));
        if (!already) await shopifyAddVariantToCart(variantId, 1);

        // Single batched call: reset context + set subscription choice
        await shopifyCartUpdateAttributes({
          ...getCheckoutResetAttrs(),
          cc_subscription_choice: "1",
          cc_has_subscription_choice: "1",
        });

        window.location.href = checkoutUrl;
      } catch (e) {
        console.error(e);
        setUpsellMsg(e?.message || "Erreur");
      } finally {
        setBtnLoading(addBtn, false);
      }
    });
  }

  if (skipBtn && skipBtn.dataset.bound !== "1") {
    skipBtn.dataset.bound = "1";
    skipBtn.addEventListener("click", async () => {
      setUpsellMsg("");
      setBtnLoading(skipBtn, true);

      try {
        await removeSubscriptionFromCartIfPresent();

        // Single batched call: reset context + set subscription skip
        await shopifyCartUpdateAttributes({
          ...getCheckoutResetAttrs(),
          cc_subscription_choice: "0",
          cc_has_subscription_choice: "0",
        });

        window.location.href = checkoutUrl;
      } catch (e) {
        console.error(e);
        setUpsellMsg(e?.message || "Erreur");
      } finally {
        setBtnLoading(skipBtn, false);
      }
    });
  }
}

/* ============================================================
   12) CHECKOUT PAYMENT
============================================================ */
function getCartIdFromCookie() {
  const raw = document.cookie.split("; ").find((c) => c.startsWith("cart="));
  if (!raw) return "";
  const val = decodeURIComponent(raw.slice("cart=".length));
  const m = val.match(/[?&]key=([^&]+)/);
  return m ? m[1] : "";
}

async function fetchCartMeta() {
  const res = await fetch("/cart?view=meta-data.json", { credentials: "same-origin" });
  const text = await res.text();
  if (!text.trim().startsWith("{")) {
    throw new Error(window.SHOP_TRANSLATIONS?.errors?.cart_meta_not_json || "cart meta-data.json ne renvoie pas du JSON.");
  }
  return JSON.parse(text);
}

function moneyToDecimalStringFromCents(cents) {
  const n = Number(cents || 0);
  return (n / 100).toFixed(2);
}

function buildCCProductsString(metaArr, { hasBenefits, hasDigitalProduct } = {}) {
  const defaultShipPriceCents = Number(window.CC_DEFAULT_SHIP_PRICE || 0);
  let productIndex = 0; // ✅ Track actual product index after filtering

  return (metaArr || []).reduce((out, item, idx0) => {
    // ✅ NEW: Skip advantages product if user has benefits
    if (hasBenefits) {
      const productHandle = String(
        item.handle || 
        item.product_handle || 
        item.product?.handle || 
        ""
      ).trim();
      
      if (productHandle === CC_ADVANTAGES_HANDLE) {
        console.log("[CC] Produit avantages exclu du checkout (utilisateur a les avantages)");
        return out; // Skip this product entirely
      }
    }

    productIndex += 1; // ✅ Increment only for products we include
    const index = productIndex;

    const raw = String(item.id || "");
    const [productId, variantId] = raw.includes(".") ? raw.split(".") : [raw, null];

    let priceCents = Number(item.price || 0);

    if (item.is_subscription) priceCents = 0;
    if (hasBenefits && !item.is_subscription) priceCents = Math.round(priceCents / 2);

    // Check if this specific product is digital (uses Shopify's native requires_shipping property)
    const isItemDigital = item.requires_shipping === false;

    // Set shipPrice to 0 if: hasBenefits OR hasDigitalProduct (coupon FREE) OR this specific item is digital
    const shipPriceCents = (hasBenefits || hasDigitalProduct || isItemDigital) ? 0 : defaultShipPriceCents;

    const price = moneyToDecimalStringFromCents(priceCents);
    const shipPrice = moneyToDecimalStringFromCents(shipPriceCents);

    const s =
      `&product${index}_id=${encodeURIComponent(productId)}` +
      (variantId ? `&variant${index}_id=${encodeURIComponent(variantId)}` : "") +
      `&product${index}_qty=${encodeURIComponent(String(item.quantity || 1))}` +
      `&product${index}_price=${encodeURIComponent(price)}` +
      `&product${index}_shipPrice=${encodeURIComponent(shipPrice)}`;

    return out + s;
  }, "");
}

function getCheckoutField(name) {
  return (
    $(`[name="${name}"]`)?.value?.trim() ||
    $(`#${name}`)?.value?.trim() ||
    ""
  );
}

function setCheckoutMsg(text, type = "error") {
  const el = $("#cc-checkout-msg");
  if (!el) return;

  if (!text) {
    el.textContent = "";
    el.classList.remove("is-visible", "cc-payment-success", "cc-payment-error");
    return;
  }

  el.textContent = decodeEntities(text);
  el.classList.add("is-visible");
  
  // Remove both classes first, then add the appropriate one
  el.classList.remove("cc-payment-success", "cc-payment-error");
  if (type === "success" || type === "processing") {
    el.classList.add("cc-payment-success");
  } else {
    el.classList.add("cc-payment-error");
  }

  requestAnimationFrame(() => {
    el.scrollIntoView({ behavior: "smooth", block: "center" });
  });
}

async function initCheckoutPay() {
  const form = $("#cc-checkout-payment");
  const honeypotInput = form?.querySelector('input[name="company"]');

  if (form && !form.dataset.noSubmit) {
    form.dataset.noSubmit = "1";
    form.addEventListener(
      "submit",
      (e) => {
        e.preventDefault();
        e.stopPropagation();
        return false;
      },
      true
    );
  }

  if (!window.location.pathname.includes("/pages/checkout")) return;

  // show error message if coming back with ?cc_error=1
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get("cc_error") === "1") {
      setCheckoutMsg(window.SHOP_TRANSLATIONS?.checkout?.payment_declined || "");

      $("#cc-checkout-msg")?.scrollIntoView({ behavior: "smooth", block: "center" });

      params.delete("cc_error");
      const cleanUrl =
        window.location.origin +
        window.location.pathname +
        (params.toString() ? "?" + params.toString() : "");
      window.history.replaceState({}, "", cleanUrl);
    }
  } catch {}

  const payBtn =
    $("#cc-pay-btn") ||
    $('[data-cc="pay"]') ||
    $('button[type="submit"][name="cc_pay"]');

  if (!payBtn) return;
  if (payBtn.dataset.bound === "1") return;
  payBtn.dataset.bound = "1";

  payBtn.addEventListener("click", async (e) => {
    e.preventDefault();
    setCheckoutMsg("");

    if (honeypotInput && honeypotInput.value.trim() !== "") return;

    const endpoint = window.CC_ORDER_COMPLETE_ENDPOINT;
    if (!endpoint) return setCheckoutMsg(window.SHOP_TRANSLATIONS?.checkout?.missing_endpoint || "CC_ORDER_COMPLETE_ENDPOINT manquant (URL du PHP order import).");

    // Build thank you page URL with locale prefix
    const localePrefix = String(window.CC_LOCALE_PREFIX || "").trim();
    const thankSlug = String(window.CC_THANKYOU_SLUG || "").trim()
      || String(CC_THANKYOU_PAGE_HANDLE_DEFAULT || "confirmation-d-achat").trim();
    const thankYouPageUrl = localePrefix + "/pages/" + thankSlug;
    const thankRedirect = String(window.CC_THANKYOU_REDIRECT_URL || "").trim();
    if (!thankRedirect) return setCheckoutMsg(window.SHOP_TRANSLATIONS?.checkout?.missing_redirect_url || "CC_THANKYOU_REDIRECT_URL manquant (URL du confirmation_achat.php).");

    // Validation serveur : email (NeverBounce) + code postal avant paiement
    if (window.CC_VERIFY_ENDPOINT) {
      const pre0 = window.CC_PREFILL || {};
      const verifyPayload = {
        emailAddress:    decodeEntities(pre0.emailAddress    || getCheckoutField("emailAddress")),
        shippingFirstName: decodeEntities(pre0.firstName    || getCheckoutField("firstName")),
        shippingLastName:  decodeEntities(pre0.lastName     || getCheckoutField("lastName")),
        shippingAddress1:  decodeEntities(pre0.address1     || getCheckoutField("address1")),
        shippingAddress2:  decodeEntities(pre0.address2     || getCheckoutField("address2")),
        shippingPostalCode: decodeEntities(pre0.postalCode  || getCheckoutField("postalCode")),
        shippingCity:      decodeEntities(pre0.city         || getCheckoutField("city")),
        shippingCountry:   decodeEntities(pre0.country      || getCheckoutField("country")),
        sessionId: getCartIdFromCookie() || ""
      };
      let verifyResult;
      try {
        const vResp = await fetch(window.CC_VERIFY_ENDPOINT, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "omit",
          body: JSON.stringify(verifyPayload)
        });
        verifyResult = vResp.ok ? await vResp.json() : null;
      } catch (_e) {
        verifyResult = null;
      }
      const emailErr = verifyResult?.errors?.emailAddress;
      const postalErr = verifyResult?.errors?.shippingPostalCode;
      if (emailErr || postalErr) {
        const errEl = document.getElementById("checkoutError");
        if (errEl) errEl.textContent = emailErr || postalErr;
        payBtn.disabled = false;
        return;
      }
    }

    try {
      payBtn.disabled = true;

      const pre = window.CC_PREFILL || {};

      const firstName = decodeEntities(pre.firstName || getCheckoutField("firstName"));
      const lastName = decodeEntities(pre.lastName || getCheckoutField("lastName"));
      const emailAddress = decodeEntities(pre.emailAddress || getCheckoutField("emailAddress"));

      const address1 = decodeEntities(pre.address1 || getCheckoutField("address1"));
      const address2 = decodeEntities(pre.address2 || getCheckoutField("address2"));
      const postalCode = decodeEntities(pre.postalCode || getCheckoutField("postalCode"));
      const city = decodeEntities(pre.city || getCheckoutField("city"));
      const country = decodeEntities(pre.country || getCheckoutField("country"));
      const phoneNumber = decodeEntities(pre.phoneNumber || getCheckoutField("phoneNumber"));

      const cardNumber = getCheckoutField("cardNumber").replace(/\s+/g, "");
      const cardMonth = getCheckoutField("cardMonth");
      const cardYear = getCheckoutField("cardYear");
      const cardSecurityCode = getCheckoutField("cardSecurityCode");

      setCheckoutMsg(window.SHOP_TRANSLATIONS?.checkout?.loading_cart || "");
      const meta = await fetchCartMeta();
      const metaArr = Array.isArray(meta?.meta_product_id_array) ? meta.meta_product_id_array : [];

      const subVariantId = String(window.CC_SUB_VARIANT_ID || "").trim();
      const cart = await shopifyGetCart();

      const active = await hasActiveSubscription(emailAddress);
      const hasSubInCart = cartHasSubscriptionProduct(cart, subVariantId);
      const hasBenefits = active || hasSubInCart;

      console.log("[CC][BENEFITS]", { emailAddress, active, hasSubInCart, hasBenefits });

      // Check if cart contains a digital product (uses Shopify's native requires_shipping property)
      const hasDigitalProduct = (() => {
        // Check metaArr first
        if (Array.isArray(metaArr)) {
          const found = metaArr.some(item => {
            if (item.is_subscription) return false;
            return item.requires_shipping === false;
          });
          if (found) return true;
        }
        
        // Fallback: check cart.items directly
        if (Array.isArray(cart?.items)) {
          return cart.items.some(item => {
            if (String(item.variant_id) === String(subVariantId)) return false;
            return item.requires_shipping === false;
          });
        }
        return false;
      })();

      // Build products string with digital product info so shipPrice is set to 0
      const products = buildCCProductsString(metaArr, { hasBenefits, hasDigitalProduct });

      const cartId = String(meta?.cartId || "").trim() || getCartIdFromCookie();
      if (!cartId) throw new Error(window.SHOP_TRANSLATIONS?.errors?.cart_id_missing || "cartId manquant (meta + cookie).");

      const thankHandle = String(window.CC_THANKYOU_PAGE_HANDLE || CC_THANKYOU_PAGE_HANDLE_DEFAULT).trim();

      const payload = {
        products,
        cartId,
        firstName,
        lastName,
        emailAddress,
        address1,
        address2,
        postalCode,
        city,
        country,
        phoneNumber,

        shipFirstName: firstName,
        shipLastName: lastName,
        shipAddress1: address1,
        shipAddress2: address2,
        shipPostalCode: postalCode,
        shipCity: city,
        shipCountry: country,
        billShipSame: "1",

        cardNumber,
        cardMonth,
        cardYear,
        cardSecurityCode,

        paySource: "CREDITCARD",
        browserData: $("#browserData")?.value || "",

        redirectsTo: thankRedirect,
        errorRedirectsTo: window.location.origin + window.location.pathname + "?cc_error=1",

        custom1: window.location.hostname,
        custom2: window.location.pathname,
        custom3: thankHandle,
        custom4: localePrefix,

        campaignId: String(window.CC_DEFAULT_CAMPAIGN_ID || ""),
      };

      // Add FREE coupon if digital product is present
      if (hasDigitalProduct) {
        payload.couponCode = "FREE";
        console.log("[CC] Digital product detected - adding FREE couponCode to payload");
      }

      const required = [
        "products",
        "cartId",
        "firstName",
        "lastName",
        "emailAddress",
        "cardNumber",
        "cardMonth",
        "cardYear",
        "cardSecurityCode",
        "redirectsTo",
        "custom1",
        "custom3",
      ];
      for (const k of required) {
        if (!payload[k]) {
          const fieldMissingMsg = window.SHOP_TRANSLATIONS?.errors?.field_missing?.replace('', k) || `Champ manquant: ${k}`;
          throw new Error(fieldMissingMsg);
        }
      }

      setCheckoutMsg(window.SHOP_TRANSLATIONS?.checkout?.processing_payment || "", "processing");

      const res = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || (window.SHOP_TRANSLATIONS?.errors?.server || "Erreur serveur."));

      switch (String(data?.result || "").toUpperCase()) {
        case "SUCCESS": {
          // ✅ Clean up session and cart attributes if user was not really logged in
          const shouldCleanup = sessionStorage.getItem("cc_cleanup_after_checkout") === "1";
          if (shouldCleanup) {
            clearSession();
            clearBenefitsCache();
            try {
              await shopifyCartUpdateAttributes({
                cc_email: "",
                cc_has_subscription: "0",
                cc_subscription_choice: "",
                cc_has_subscription_choice: "0",
              });
            } catch (e) {
              console.warn("[CC] Failed to clean cart attributes:", e);
            }
            sessionStorage.removeItem("cc_cleanup_after_checkout");
          }
          
          // Relay through confirmation_achat.php (sends confirmation email + has fallback page)
          const msg = data.message || {};
          if (!msg.custom1) msg.custom1 = window.location.hostname;
          if (!msg.custom3) msg.custom3 = String(window.CC_THANKYOU_PAGE_HANDLE || CC_THANKYOU_PAGE_HANDLE_DEFAULT).trim();
          if (!msg.custom4) msg.custom4 = localePrefix;
          if (!msg.emailAddress) msg.emailAddress = emailAddress;
          if (!msg.firstName)    msg.firstName    = firstName;
          if (!msg.lastName)     msg.lastName     = lastName;
          const encoded = jsonToBase64Utf8(msg);
          window.location.replace(`${thankRedirect}?orderData=${encodeURIComponent(encoded)}`);
          break;
        }
        case "ERROR":
          setCheckoutMsg(data?.message?.message || data?.message || (window.SHOP_TRANSLATIONS?.checkout?.payment_declined_short || ""));
          break;

        case "MERC_REDIRECT":
          if (data?.message?.url) window.location.href = data.message.url;
          else if (data?.message?.script) eval(data.message.script);
          else setCheckoutMsg(window.SHOP_TRANSLATIONS?.checkout?.missing_3ds_redirect || "Redirection 3DS manquante.");
          break;

        default:
          console.error("Unexpected response", data);
          setCheckoutMsg(window.SHOP_TRANSLATIONS?.checkout?.unknown_response || "Réponse inconnue.");
      }
    } catch (err) {
      console.error(err);
      setCheckoutMsg(err?.message || (window.SHOP_TRANSLATIONS?.errors?.checkout || "Erreur checkout."));
    } finally {
      payBtn.disabled = false;
    }
  });
}

/* ============================================================
   13) ACCOUNT / POPUP UI
============================================================ */
function showLogin() {
  $("#cc-login-block") && ($("#cc-login-block").style.display = "flex");
  $("#cc-account-block") && ($("#cc-account-block").style.display = "none");
}

function showAccount() {
  $("#cc-login-block") && ($("#cc-login-block").style.display = "none");
  $("#cc-account-block") && ($("#cc-account-block").style.display = "block");
}

function setAccountMsg(text, type = "") {
  const msg = $("#cc-account-msg");
  if (!msg) return;

  const clean = String(text || "").trim();
  msg.textContent = clean;

  msg.classList.remove("is-error", "is-success");
  if (!clean) return;

  if (type === "error") msg.classList.add("is-error");
  if (type === "success") msg.classList.add("is-success");
}

function setMemberMsg(text) {
  const msg = $("#cc-member-msg");
  if (msg) msg.textContent = text || "";
}

function openApiPopup({ title = window.SHOP_TRANSLATIONS?.popup?.title_information || "Information", html = "" } = {}) {
  const modal = $("#CC-ApiPopup");
  const t = $("#CC-ApiPopupTitle");
  const b = $("#CC-ApiPopupBody");
  if (!modal || !t || !b) return false;

  t.textContent = decodeEntities(title);
  b.innerHTML = html;

  modal.classList.add("modal--is-active");
  modal.setAttribute("aria-hidden", "false");
  return true;
}

function closeApiPopup() {
  const modal = $("#CC-ApiPopup");
  if (!modal) return;
  modal.classList.remove("modal--is-active");
  modal.setAttribute("aria-hidden", "true");
}

function openApiPopupAndWait(opts) {
  return new Promise((resolve) => {
    const modal = $("#CC-ApiPopup");
    const closeBtn = modal?.querySelector(".js-modal-close");
    const titleEl = $("#CC-ApiPopupTitle");
    const bodyEl = $("#CC-ApiPopupBody");

    if (!modal || !closeBtn || !titleEl || !bodyEl) {
      resolve();
      return;
    }

    openApiPopup(opts);

    const cleanup = () => {
      closeBtn.removeEventListener("click", onClose);
      modal.removeEventListener("click", onBackdrop);
      resolve();
    };

    const onClose = () => {
      closeApiPopup();
      cleanup();
    };

    const onBackdrop = (e) => {
      if (e.target === modal) {
        closeApiPopup();
        cleanup();
      }
    };

    closeBtn.addEventListener("click", onClose);
    modal.addEventListener("click", onBackdrop);
  });
}

function isActiveSubscription(purchase) {
  const s = String(purchase?.status || "").toUpperCase();
  return s === "TRIAL" || s === "ACTIVE" || s === "RECYCLE_BILLING";
}

/* ============================================================
   14) ACCOUNT — ADDRESS FORM
============================================================ */
function setAddressMsg(text) {
  const el = $("#cc-address-msg");
  if (el) el.textContent = decodeEntities(text) || "";
}

function fillAddressForm(customer) {
  const form = $("#cc-address-form");
  if (!form) return;

  const saved = getAddress();
  const source = saved || {
    firstName: customer?.firstName || "",
    lastName: customer?.lastName || "",
    address1: customer?.shipAddress1 || "",
    postalCode: customer?.postalCode || "",
    city: customer?.shipCity || "",
    countryCode: customer?.country_code || customer?.country || "",
    phone: customer?.phoneNumber || "",
  };

  Object.entries(source).forEach(([k, v]) => {
    const input = form.querySelector(`[name="${k}"]`);
    if (input && v != null) {
      var tmp = document.createElement('textarea');
      tmp.innerHTML = String(v);
      input.value = tmp.value;
    }
  });
}

function initAddressForm() {
  const form = $("#cc-address-form");
  if (!form) return;

  if (form.dataset.bound === "1") return;
  form.dataset.bound = "1";

  form.addEventListener("submit", (e) => {
    e.preventDefault();
    setAddressMsg("");

    const fd = new FormData(form);
    const address = {
      firstName: String(fd.get("firstName") || "").trim(),
      lastName: String(fd.get("lastName") || "").trim(),
      address1: String(fd.get("address1") || "").trim(),
      postalCode: String(fd.get("postalCode") || "").trim(),
      city: String(fd.get("city") || "").trim(),
      countryCode: String(fd.get("countryCode") || "").trim(),
      phone: String(fd.get("phone") || "").trim(),
    };

    const missing = Object.values(address).some((v) => !v);
    if (missing) return setAddressMsg(window.SHOP_TRANSLATIONS?.popup?.message_fill_all_fields || "Veuillez remplir tous les champs.");

    saveAddress(address);
    setAddressMsg(window.SHOP_TRANSLATIONS?.popup?.message_address_saved || "Adresse enregistrée ✅");
  });
}

/* ============================================================
   15) ACCOUNT — RENDER + TABS + ACTIONS
============================================================ */
function initTabs() {
  const tabs = $$(".cc-tab");
  const panels = $$(".cc-panel");
  if (!tabs.length || !panels.length) return;

  const activate = (key) => {
    tabs.forEach((t) => {
      const on = t.dataset.tab === key;
      t.classList.toggle("is-active", on);
      t.setAttribute("aria-selected", on ? "true" : "false");
    });
    panels.forEach((p) => {
      const on = p.dataset.panel === key;
      p.classList.toggle("is-active", on);
    });
  };

  tabs.forEach((t) => t.addEventListener("click", () => activate(t.dataset.tab)));
  activate(tabs[0].dataset.tab);
}

function wireMemberButtons({ active }) {
  const session = getSession() || {};

  const subBtn = $("#cc-subscription-action-btn");
  const youBtn = $("#cc-youscribe-btn");
  const buyBtn = $("#cc-buy-subscription");

  if (subBtn && subBtn.dataset.bound !== "1") {
    subBtn.dataset.bound = "1";
    subBtn.addEventListener("click", async () => {
      setMemberMsg(window.SHOP_TRANSLATIONS?.popup?.message_processing || "");

      const email = session.email || "";
      const orderId = session.orderId || "";
      const language = getShopifyLocale();
      const customerId = session.customerId || "";
      const purchaseId = session.purchaseId || "";
      const action = subBtn.dataset.action || (active ? "cancel" : "activate");

      if (!email || !orderId) return setMemberMsg(window.SHOP_TRANSLATIONS?.popup?.message_missing_credentials || "Identifiants manquants.");
      if (!customerId) return setMemberMsg(window.SHOP_TRANSLATIONS?.popup?.message_customer_id_missing || "customerId manquant.");
      if (!purchaseId) return setMemberMsg(window.SHOP_TRANSLATIONS?.popup?.message_purchase_id_missing || "purchaseId manquant.");

      try {
        const resp = await postJSON("/action_sur_abonnement.php", {
          email,
          action,
          purchaseId,
          customerId,
          language,
        });

        // Check if message is about already being unsubscribed and use translation
        const messageText = Array.isArray(resp.messages) ? resp.messages.map(cleanApiMsg).join(" ") : cleanApiMsg(resp.message || "");
        const isAlreadyUnsubscribed = messageText.includes("déjà désabonné") || messageText.includes("already unsubscribed") || messageText.toLowerCase().includes("désabonné");
        
        const html = isAlreadyUnsubscribed
          ? `<p>${window.SHOP_TRANSLATIONS?.popup?.message_subscription_already_unsubscribed || messageText}</p>`
          : Array.isArray(resp.messages)
          ? resp.messages.map((m) => `<p>${cleanApiMsg(m)}</p>`).join("")
          : `<p>${cleanApiMsg(resp.message || "")}</p>`;

        await openApiPopupAndWait({ title: window.SHOP_TRANSLATIONS?.popup?.title_subscription || "Abonnement", html });
        await loadAccountFromSession();
      } catch (e) {
        await openApiPopupAndWait({
          title: window.SHOP_TRANSLATIONS?.popup?.title_error || "Erreur",
          html: `<p>${cleanApiMsg(e?.message) || window.SHOP_TRANSLATIONS?.popup?.title_error || "Erreur"}</p>`,
        });
      } finally {
        setMemberMsg("");
      }
    });
  }

  if (youBtn && youBtn.dataset.bound !== "1") {
    youBtn.dataset.bound = "1";
    youBtn.addEventListener("click", async () => {
      setMemberMsg(window.SHOP_TRANSLATIONS?.popup?.message_generating_code || "Génération du code...");

      const email = session.email || "";
      const orderId = session.orderId || "";
      const customerId = session.customerId || "";
      const language = getShopifyLocale();

      if (!email || !orderId) return setMemberMsg(window.SHOP_TRANSLATIONS?.popup?.message_missing_credentials || "Identifiants manquants.");

      try {
        const resp = await postJSON("/generer_un_code_ebook.php", {
          email,
          orderId,
          customerId,
          domaine: window.location.hostname,
          language,
        });

        await openApiPopupAndWait({
          title: window.SHOP_TRANSLATIONS?.popup?.title_youscribe_code || "Code Youscribe",
          html: `<p>${cleanApiMsg(resp.message) || window.SHOP_TRANSLATIONS?.popup?.message_code_generated || "Code généré."}</p>`,
        });
      } catch (e) {
        await openApiPopupAndWait({
          title: window.SHOP_TRANSLATIONS?.popup?.title_error || "Erreur",
          html: `<p>${cleanApiMsg(e?.message) || window.SHOP_TRANSLATIONS?.popup?.title_error || "Erreur"}</p>`,
        });
      } finally {
        setMemberMsg("");
      }
    });
  }

  if (buyBtn && buyBtn.dataset.bound !== "1") {
    buyBtn.dataset.bound = "1";
    buyBtn.addEventListener("click", async () => {
      await openApiPopupAndWait({
        title: window.SHOP_TRANSLATIONS?.popup?.title_subscription || "Abonnement",
        html: `<p>${window.SHOP_TRANSLATIONS?.popup?.message_todo_add_subscription || "TODO: ajouter l'abonnement au panier (variantId Shopify)."}</p>`,
      });
    });
  }
}

function renderAccount(data) {
  const out = $("#cc-account-output");
  if (!out) return;

  // ✅ UPDATED: Use cc_account from translations (matches the JSON structure)
  // Note: API data (product_name, purchase.productName, purchase.label, purchase.text) 
  // should be translated by the API using shopify_locale parameter sent via postJSON()
  const I18N = window.CC_I18N?.cc_account || {};
  const t = (key, fallback = "") => {
    const parts = String(key || "").split(".");
    let cur = I18N;
    for (const p of parts) cur = cur && Object.prototype.hasOwnProperty.call(cur, p) ? cur[p] : undefined;
    return typeof cur === "string" && cur.trim() !== "" ? cur : fallback;
  };
  
  // Helper to get translation without hard-coded fallbacks (all translations come from JSON files)
  const translate = (key) => {
    const translated = t(key);
    if (translated) return translated;
    console.warn(`[CC] Missing translation: cc_account.${key}`);
    return ""; // Return empty instead of hard-coded French
  };

  const esc = (v) =>
    decodeEntities(v).replace(/[&<>"']/g, (c) => ({
      "&": "&amp;",
      "<": "&lt;",
      ">": "&gt;",
      '"': "&quot;",
      "'": "&#39;",
    }[c]));

  const customerId = data?.customer?.customerId || "";
  const purchase = data?.purchase || null;

  patchSession({
    customerId,
    purchaseId: purchase?.purchaseId || "",
  });

  const active = isActiveSubscription(purchase);

  const ordersRows = (data.orders || [])
    .map((o) => {
      const items = (o.items || []).map((it) => esc(it.product_name)).join(", ");
      return `
        <tr>
          <td>${esc(translate("orders.ref_prefix"))} ${esc(o.orderId)}</td>
          <td>${esc(o.dateCreated || "-")}</td>
          <td>${items || "-"}</td>
          <td><strong>${esc(o.totalAmount)} ${esc(o.currencyCode)}</strong></td>
        </tr>
      `;
    })
    .join("");

  // API should return translated data based on shopify_locale, but use fallback translations if not
  const subLine = purchase
    ? `${esc(purchase.productName || translate("subscription.title"))} — ${esc(purchase.label || "")}${purchase.date ? ` : ${esc(purchase.date)}` : ""}`
    : esc(translate("subscription.none"));

  const subActionBtn = purchase
    ? `<button id="cc-subscription-action-btn" type="button" class="btn" data-action="${esc(purchase.action || "")}">
        ${purchase.action === "cancel" ? esc(translate("subscription.action_cancel")) : purchase.action === "activate" ? esc(translate("subscription.action_activate")) : esc(translate("subscription.action_fallback"))}
      </button>`
    : "";

  const youscribeBtn = active
    ? `<button id="cc-youscribe-btn" type="button" class="btn btn--secondary">
        ${esc(translate("subscription.youscribe_btn"))}
      </button>`
    : "";

  const youscribeLoginBtn = active
    ? `<a id="cc-youscribe-login-btn" href="https://www.youscribe.com/Account/Login/LogOn" target="_blank" rel="noopener noreferrer" class="btn cc-youscribe-login-btn">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
        ${esc(translate("subscription.youscribe_login_btn"))}
      </a>`
    : "";

  const buyBtn = !active
    ? `<button id="cc-buy-subscription" type="button" class="btn btn--secondary">
        ${esc(translate("subscription.buy_btn"))}
      </button>`
    : "";

  out.innerHTML = `
    <div class="cc-dash">
      <aside class="cc-dash__side" role="tablist" aria-label="${esc(translate("dashboard.aria"))}">
        <button class="cc-tab is-active" type="button" data-tab="profile" role="tab" aria-selected="true">
          <span class="cc-tab__icon" aria-hidden="true"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg></span>
          ${esc(translate("dashboard.tabs.profile"))}
        </button>
        <button class="cc-tab" type="button" data-tab="orders" role="tab" aria-selected="false">
          <span class="cc-tab__icon" aria-hidden="true"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg></span>
          ${esc(translate("dashboard.tabs.orders"))}
        </button>
        <button class="cc-tab" type="button" data-tab="subscription" role="tab" aria-selected="false">
          <span class="cc-tab__icon" aria-hidden="true"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg></span>
          ${esc(translate("dashboard.tabs.subscription"))}
        </button>
      </aside>

      <div class="cc-dash__main">

        <!-- PROFILE PANEL -->
        <section class="cc-panel is-active" data-panel="profile" role="tabpanel">
          <div class="cc-panel__header">
            <span class="cc-panel__header-icon" aria-hidden="true"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg></span>
            <h2 class="cc-panel__title">${esc(translate("profile.title"))}</h2>
          </div>

          <div class="cc-info-cards">
            <div class="cc-info-card">
              <div class="cc-info-card__label">${esc(translate("profile.name"))}</div>
              <div class="cc-info-card__value">${esc(data.customer?.firstName || "")} ${esc(data.customer?.lastName || "")}</div>
            </div>
            <div class="cc-info-card">
              <div class="cc-info-card__label">${esc(translate("profile.email"))}</div>
              <div class="cc-info-card__value">${esc(data.customer?.emailAddress || "")}</div>
            </div>
          </div>

          <div class="cc-address-section">
            <p class="cc-address-section__title">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 21c-4-4-8-8-8-12a8 8 0 0 1 16 0c0 4-4 8-8 12z"/><circle cx="12" cy="9" r="2.5"/></svg>
              ${esc(translate("profile.shipping_title"))}
            </p>

            <form id="cc-address-form" class="form-vertical" novalidate>
              <div class="grid">
                <div class="grid__item medium-up--one-half">
                  <label>${esc(translate("profile.first_name"))}</label>
                  <input class="input-full" name="firstName" autocomplete="given-name" disabled>
                </div>
                <div class="grid__item medium-up--one-half">
                  <label>${esc(translate("profile.last_name"))}</label>
                  <input class="input-full" name="lastName" autocomplete="family-name" disabled>
                </div>
                <div class="grid__item one-whole">
                  <label>${esc(translate("profile.address"))}</label>
                  <input class="input-full" name="address1" autocomplete="street-address" disabled>
                </div>
                <div class="grid__item medium-up--one-half">
                  <label>${esc(translate("profile.postal_code"))}</label>
                  <input class="input-full" name="postalCode" autocomplete="postal-code" disabled>
                </div>
                <div class="grid__item medium-up--one-half">
                  <label>${esc(translate("profile.city"))}</label>
                  <input class="input-full" name="city" autocomplete="address-level2" disabled>
                </div>
                <div class="grid__item medium-up--one-half">
                  <label>${esc(translate("profile.country"))}</label>
                  <select class="input-full" name="countryCode" autocomplete="country" disabled>
                    <option value="">${esc(translate("profile.country_placeholder"))}</option>
                    <option value="FR">${esc(translate("profile.countries.FR"))}</option>
                    <option value="BE">${esc(translate("profile.countries.BE"))}</option>
                    <option value="CH">${esc(translate("profile.countries.CH"))}</option>
                    <option value="LU">${esc(translate("profile.countries.LU"))}</option>
                  </select>
                </div>
                <div class="grid__item medium-up--one-half">
                  <label>${esc(translate("profile.phone"))}</label>
                  <input class="input-full" name="phone" type="tel" autocomplete="tel" disabled>
                </div>
              </div>
              <p id="cc-address-msg" class="note"></p>
            </form>
          </div>
        </section>

        <!-- ORDERS PANEL -->
        <section class="cc-panel" data-panel="orders" role="tabpanel">
          <div class="cc-panel__header">
            <span class="cc-panel__header-icon" aria-hidden="true"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg></span>
            <h2 class="cc-panel__title">${esc(translate("orders.title"))}</h2>
          </div>

          <div class="cc-table-wrap">
            <table class="cc-table">
              <thead>
                <tr>
                  <th>${esc(translate("orders.th_order"))}</th>
                  <th>${esc(translate("orders.th_date"))}</th>
                  <th>${esc(translate("orders.th_items"))}</th>
                  <th>${esc(translate("orders.th_amount"))}</th>
                </tr>
              </thead>
              <tbody>
                ${ordersRows || `<tr><td colspan="4">${esc(translate("orders.empty"))}</td></tr>`}
              </tbody>
            </table>
          </div>
        </section>

        <!-- SUBSCRIPTION PANEL -->
        <section class="cc-panel" data-panel="subscription" role="tabpanel">
          <div class="cc-panel__header">
            <span class="cc-panel__header-icon" aria-hidden="true"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg></span>
            <h2 class="cc-panel__title">${esc(translate("subscription.title"))}</h2>
          </div>

          <!-- Bloc statut abonnement -->
          <div class="cc-sub-card">
            <div class="cc-sub-card__status ${active ? 'cc-sub-card__status--active' : 'cc-sub-card__status--inactive'}">
              ${active
                ? `<svg width="8" height="8" viewBox="0 0 8 8" fill="currentColor"><circle cx="4" cy="4" r="4"/></svg> ${esc(translate("subscription.status_active"))}`
                : `<svg width="8" height="8" viewBox="0 0 8 8" fill="currentColor" opacity=".4"><circle cx="4" cy="4" r="4"/></svg> ${esc(translate("subscription.status_inactive"))}`}
            </div>
            <p class="cc-sub-card__name">${purchase ? esc(purchase.productName || translate("subscription.title")) : esc(translate("subscription.none"))}</p>
            ${purchase ? `<p class="cc-sub-card__detail">${esc(purchase.label || "")}${purchase.date ? ` — ${esc(purchase.date)}` : ""}</p>` : ""}

            <div class="cc-actions" style="margin-top:14px;">
              ${subActionBtn}
              ${buyBtn}
            </div>
            <p id="cc-member-msg" class="note cc-member-msg"></p>
          </div>

          ${active ? `
          <!-- Bloc Youscribe -->
          <div class="cc-youscribe-card">
            <div class="cc-youscribe-card__header">
              <div class="cc-youscribe-card__logo" aria-hidden="true">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg>
              </div>
              <h3 class="cc-youscribe-card__title">${esc(translate("subscription.youscribe_title"))}</h3>
            </div>

            <p class="cc-youscribe-card__desc">${esc(translate("subscription.youscribe_desc"))}</p>

            <ol class="cc-youscribe-steps">
              <li class="cc-youscribe-steps__item">
                <span class="cc-youscribe-steps__num">1</span>
                <span>${esc(translate("subscription.youscribe_step1"))}</span>
              </li>
              <li class="cc-youscribe-steps__item">
                <span class="cc-youscribe-steps__num">2</span>
                <span>${esc(translate("subscription.youscribe_step2"))}</span>
              </li>
            </ol>

            <div class="cc-youscribe-card__actions">
              ${youscribeBtn}
              ${youscribeLoginBtn}
            </div>
          </div>
          ` : ""}

        </section>

      </div>
    </div>
  `;

  initTabs();
  wireMemberButtons({ active });
  fillAddressForm(data.customer);
  initAddressForm();
}

/* ============================================================
   16) CANCEL SUBSCRIPTION (PAGE TEMPLATE)
============================================================ */
function initCancelSubscriptionPage() {
  const form = $("#cc-unsub-form");
  if (!form) return;

  if (form.dataset.bound === "1") return;
  form.dataset.bound = "1";

  const msg = $("#cc-msg");
  const btn = $("#cc-submit") || form.querySelector('button[type="submit"], input[type="submit"]');

  const starsWrap = form.querySelector("[data-stars]");
  const stars = starsWrap ? Array.from(starsWrap.querySelectorAll(".cc-star")) : [];
  const ratingInput = form.querySelector("#cc-rating");
  const hint = form.querySelector("#cc-stars-hint");

  const emailInput = form.querySelector("#cc-email");
  const reasonInput = form.querySelector("#cc-reason");

  const honeypotInput = form.querySelector('input[name="company"]');
  const SITE_KEY = window.CC_SITE_KEY || "ecolizo";

  function setInlineMsg(text, ok) {
    if (!msg) return;
    msg.textContent = text || "";
    msg.classList.remove("is-ok", "is-err");
    if (text) msg.classList.add(ok ? "is-ok" : "is-err");
  }

  function clearFieldErrors() {
    emailInput?.classList.remove("cc-field-error");
    reasonInput?.classList.remove("cc-field-error");
    starsWrap?.classList.remove("cc-field-error");
  }

  function markError(el) {
    el?.classList?.add("cc-field-error");
  }

  function setStars(val) {
    const safe = Math.max(0, Math.min(5, Number(val || 0)));
    ratingInput.value = String(safe);

    stars.forEach((b) => {
      const v = Number(b.dataset.value || 0);
      b.classList.toggle("is-active", v <= safe && safe > 0);
    });

    if (hint) hint.textContent = safe ? `${safe}/5` : (window.SHOP_TRANSLATIONS?.popup?.rating_click_to_rate || "Cliquez pour noter");
  }

  function setStarsHintRequired(required) {
    if (!hint) return;
    const clickToRate = window.SHOP_TRANSLATIONS?.popup?.rating_click_to_rate || "Cliquez pour noter";
    const ratingRequired = window.SHOP_TRANSLATIONS?.popup?.rating_required || "Note obligatoire";
    hint.textContent = required
      ? ratingRequired
      : ratingInput.value > 0
      ? `${ratingInput.value}/5`
      : clickToRate;
  }

  stars.forEach((b) => {
    b.addEventListener("click", () => {
      setStars(b.dataset.value);
      starsWrap?.classList.remove("cc-field-error");
      setStarsHintRequired(false);
      setInlineMsg("", true);
    });
  });

  emailInput?.addEventListener("input", () => {
    emailInput.classList.remove("cc-field-error");
    setInlineMsg("", true);
  });

  reasonInput?.addEventListener("input", () => {
    reasonInput.classList.remove("cc-field-error");
    setInlineMsg("", true);
  });

  setStars(0);

  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    clearFieldErrors();

    if (honeypotInput && honeypotInput.value.trim() !== "") {
      setInlineMsg(window.SHOP_TRANSLATIONS?.popup?.message_request_sent || "Demande envoyée.", true);
      return;
    }

    const email = emailInput?.value?.trim() || "";
    const commentaire = reasonInput?.value?.trim() || "";
    const rating = Number(ratingInput?.value || 0);

    const emailMissing = !email;
    const ratingMissing = rating < 1 || rating > 5;
    const reasonMissing = !commentaire;

    if (emailMissing) markError(emailInput);
    if (ratingMissing) {
      markError(starsWrap);
      setStarsHintRequired(true);
    }
    if (reasonMissing) markError(reasonInput);

    if (emailMissing || ratingMissing || reasonMissing) {
      const message = emailMissing
        ? (window.SHOP_TRANSLATIONS?.popup?.error_email_required || "Veuillez renseigner votre e-mail.")
        : ratingMissing
        ? (window.SHOP_TRANSLATIONS?.popup?.error_rating_required || "Veuillez sélectionner une note (1 à 5 étoiles).")
        : (window.SHOP_TRANSLATIONS?.popup?.error_reason_required || "Veuillez écrire un message avant d'envoyer votre demande.");
      setInlineMsg(message, false);
      return;
    }

    setInlineMsg(window.SHOP_TRANSLATIONS?.popup?.message_sending || "Envoi en cours...", true);
    if (btn) btn.disabled = true;

    try {
      const data = await postJSON("/resiliation_abonnement.php", {
        email,
        language: getShopifyLocale(),
        rating,
        commentaire,
        site_key: SITE_KEY,
      });

      await openApiPopupAndWait({
        title: window.SHOP_TRANSLATIONS?.popup?.title_unsubscribe || "Désabonnement",
        html: `<p>${cleanApiMsg(data.message) || window.SHOP_TRANSLATIONS?.popup?.message_request_sent || "Demande envoyée."}</p>`,
      });

      setInlineMsg(cleanApiMsg(data.message) || window.SHOP_TRANSLATIONS?.popup?.message_request_sent || "Demande envoyée.", !!data.success);

      if (data.success) {
        form.reset();
        setStars(0);
        clearFieldErrors();
      }

      const session = getSession();
      if (session?.email === email) {
        await loadAccountFromSession();
      }
    } catch (err) {
      await openApiPopupAndWait({
        title: window.SHOP_TRANSLATIONS?.popup?.title_error || "Erreur",
        html: `<p>${err?.message || window.SHOP_TRANSLATIONS?.popup?.title_error || "Erreur"}</p>`,
      });
      setInlineMsg(err?.message || window.SHOP_TRANSLATIONS?.popup?.title_error || "Erreur", false);
    } finally {
      if (btn) btn.disabled = false;
    }
  });
}

/* ============================================================
   17) ACCOUNT — LOAD + LOGIN/LOGOUT
============================================================ */
async function loadAccountFromSession() {
  // Only call the API when the account UI is present on this page.
  // Avoids unnecessary requests on homepage, product pages, etc.
  const loginBlock = $("#cc-login-block");
  const accountBlock = $("#cc-account-block");
  if (!loginBlock && !accountBlock) return;

  const session = getSession();
  if (!session?.email || !session?.orderId) {
    showLogin();
    return;
  }

  showAccount();
  setAccountMsg(window.SHOP_TRANSLATIONS?.account?.loading || "");

  try {
    const data = await postJSON("/espace_client.php", session);
    setAccountMsg("");
    renderAccount(data);
  } catch (err) {
    // Do NOT clear the session on transient errors (network failures,
    // server timeouts, 500s). The user can retry or log out manually.
    // Clearing the session here was causing users to appear disconnected
    // after any single API hiccup.
    setAccountMsg(err?.message || (window.SHOP_TRANSLATIONS?.errors?.server || "Erreur"), "error");
    showAccount();
  }
}

function initCancelForm() {
  const form = $("#cc-cancel-form");
  if (!form) return;

  if (form.dataset.bound === "1") return;
  form.dataset.bound = "1";

  const msg = $("#cc-cancel-msg");
  const emailInput = form.querySelector('[name="email"]');
  const honeypotInput = form.querySelector('input[name="company"]');

  form.addEventListener("submit", async (e) => {
    e.preventDefault();

    if (honeypotInput && honeypotInput.value.trim() !== "") {
      if (msg) msg.textContent = window.SHOP_TRANSLATIONS?.popup?.message_request_sent || "Demande envoyée.";
      return;
    }

    const email = (emailInput?.value || "").trim();
    if (!email) {
      if (msg) msg.textContent = window.SHOP_TRANSLATIONS?.popup?.message_please_enter_email || "Veuillez saisir votre email.";
      emailInput?.focus?.();
      return;
    }

    if (msg) msg.textContent = window.SHOP_TRANSLATIONS?.popup?.message_sending || "Envoi en cours...";

    try {
      const data = await postJSON("/resiliation_abonnement.php", {
        email,
        language: getShopifyLocale(),
      });

      await openApiPopupAndWait({
        title: window.SHOP_TRANSLATIONS?.popup?.title_subscription || "Abonnement",
        html: `<p>${cleanApiMsg(data.message) || window.SHOP_TRANSLATIONS?.popup?.message_request_sent || "Demande envoyée"}</p>`,
      });

      const session = getSession();
      if (session?.email === email) {
        await loadAccountFromSession();
      }
    } catch (err) {
      await openApiPopupAndWait({
        title: window.SHOP_TRANSLATIONS?.popup?.title_error || "Erreur",
        html: `<p>${err?.message || window.SHOP_TRANSLATIONS?.popup?.title_error || "Erreur"}</p>`,
      });
    } finally {
      if (msg) msg.textContent = "";
    }
  });
}

function initLoginForm() {
  const form = $("#cc-account-form");
  if (!form) return;

  if (form.dataset.bound === "1") return;
  form.dataset.bound = "1";

  const honeypot = form.querySelector('input[name="company"]');

  form.addEventListener("submit", async (e) => {
    e.preventDefault();

    if (honeypot && honeypot.value.trim() !== "") {
      setAccountMsg("");
      return;
    }

    setAccountMsg(window.SHOP_TRANSLATIONS?.account?.connecting || "");

    const email = form.querySelector('[name="email"]')?.value?.trim() || "";
    const orderId = form.querySelector('[name="orderId"]')?.value?.trim() || "";

    try {
      const locale = getShopifyLocale();
      await postJSON("/espace_client.php", {
        email,
        orderId,
        language: locale,
      });

      saveSession(email, orderId, locale);
      clearBenefitsCache();

      // benefits + pricing refresh
      await resolveBenefitsAndApply();
      await loadAccountFromSession();
    } catch (err) {
      setAccountMsg(err?.message || (window.SHOP_TRANSLATIONS?.errors?.connection || ""), "error");
      showLogin();
    }
  });
}

function initLogout() {
  const logoutBtn = $("#cc-logout-btn");
  if (!logoutBtn) return;

  if (logoutBtn.dataset.bound === "1") return;
  logoutBtn.dataset.bound = "1";

  logoutBtn.addEventListener("click", async () => {
    // 1) Clear local state
    clearSession();
    clearBenefitsCache();

    // 2) Turn off benefits UI flag
    setBenefitsState(false);

    // 3) Reset cart attributes that can force computeHasBenefits() => true
    try {
      await shopifyCartUpdateAttributes({
        cc_has_subscription: "0",
        cc_subscription_choice: "0",
        cc_has_subscription_choice: "0",
      });
    } catch (e) {
      console.warn("[CC] logout cart attributes reset failed:", e);
    }

    // 4) Recompute + re-apply (base)
    try {
      await window.CC_REFRESH_PRICING?.();
    } catch (e) {
      console.warn("[CC] pricing refresh failed:", e);
    }

    setAccountMsg("");
    showLogin();
  });
}

/* ============================================================
   18) BOOTSTRAP
============================================================ */
document.addEventListener("DOMContentLoaded", async () => {
  // Clean cart attributes and cache if user is not logged in (fixes cache issue)
  await cleanCartAttributesIfNotLoggedIn();
  
  // pricing + benefits in a single pass
  await initGlobalPricing();

  // pages / UI
  initCancelSubscriptionPage();
  initCancelForm();
  initCartDrawerAddressAccordion();
  initLoginForm();
  initLogout();

  // cart flows
  initCartDrawerCheckout();
  initUpsellPage();

  // account + checkout
  await loadAccountFromSession();
  await initCheckoutPay();
});

/* ============================================================
   CC — Limit subscription variant quantity to max 1 in cart
============================================================ */
(function () {
  if (window.__ccCartSubscriptionQtyLimitInstalled) return;
  window.__ccCartSubscriptionQtyLimitInstalled = true;

  const subVariantId = String(window.CC_SUB_VARIANT_ID || "56310092661084").trim();
  if (!subVariantId) return;

  function limitSubscriptionQuantity() {
    document.querySelectorAll('.cart__item[data-variant-id="' + subVariantId + '"]').forEach(function (cartItem) {
      const qtyWrapper = cartItem.querySelector('.js-qty__wrapper');
      if (!qtyWrapper) return;

      const qtyInput = qtyWrapper.querySelector('.js-qty__num');
      const qtyPlus = qtyWrapper.querySelector('.js-qty__adjust--plus');

      if (!qtyInput) return;

      const currentQty = parseInt(qtyInput.value || "1", 10);

      qtyInput.setAttribute('max', '1');

      if (qtyPlus) {
        if (currentQty >= 1) {
          qtyPlus.disabled = true;
          qtyPlus.style.opacity = '0.5';
          qtyPlus.style.cursor = 'not-allowed';
        } else {
          qtyPlus.disabled = false;
          qtyPlus.style.opacity = '';
          qtyPlus.style.cursor = '';
        }
      }

      if (currentQty > 1) {
        qtyInput.value = "1";
      }
    });
  }

  document.addEventListener('cart:open', function () {
    setTimeout(limitSubscriptionQuantity, 100);
  });

  document.addEventListener('cart:updated', function () {
    setTimeout(limitSubscriptionQuantity, 100);
  });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", limitSubscriptionQuantity);
  } else {
    limitSubscriptionQuantity();
  }

  // Watch for dynamically added cart items
  const cartContainers = document.querySelectorAll('[data-products]');
  cartContainers.forEach(function (container) {
    const mo = new MutationObserver(function (mutations) {
      const hasItemChanges = mutations.some(function (mutation) {
        if (mutation.type !== 'childList') return false;
        const nodes = Array.from(mutation.addedNodes).concat(Array.from(mutation.removedNodes));
        return nodes.some(function (node) {
          if (node.nodeType !== 1) return false;
          return node.classList?.contains('cart__item') || node.querySelector?.('.cart__item');
        });
      });
      if (hasItemChanges) {
        setTimeout(limitSubscriptionQuantity, 100);
      }
    });
    mo.observe(container, { childList: true, subtree: true });
  });
})();

